import { useState } from "react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { SendWidget } from "../components/SendWidget";
import { RecentX402Reviews } from "../components/x402/RecentX402Reviews";

/*
 * Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5
 * Hallmark · macrostructure: Quiet Action · theme: boon warm paper
 * Restraint pass: the widget is the sequence; the page just frames it.
 * Centered single column matches /claim. No 01/02/03 ladder fighting the form.
 */
export const X402_REVIEW_PROMPT = `Load the Boon skill: https://docs.boonprotocol.com/skill.md

Use its x402 review workflow to review the last x402 tool you used. Include the task, observed outcome, and strongest honest evidence.

Check whether the Boon CLI is available with: boon --version

If it is missing, do not install it yourself. Ask the user to install @velinussage/boon-cli.

If the call used boon x402 fetch and produced a client_settlement activity ID, prefer the free verified-use-backed lane. Draft it first with:
boon x402 review use <activity-id> --profile <profile-id> --text "<short specific review>" --json

Show the exact activity, route, settlement, signer, review text, evidence label, and zero publication charge. Only after the user approves that exact row may you rerun it with --confirm-subjective --yes.

Use receipt-verified, Boon-backed, or self-reported lanes only when their stated evidence actually exists. Stop at preview if signing authority or evidence is missing. Ask before every signature, review publication, paid action, or Boon.`;

export function SendPage() {
  const [promptCopied, setPromptCopied] = useState(false);

  async function copyReviewPrompt() {
    await copyText(X402_REVIEW_PROMPT);
    setPromptCopied(true);
  }

  return (
    <>
      <Nav current="send" />
      <main className="overflow-x-clip">
        <section className="px-6 md:px-10 pt-12 md:pt-24 max-w-2xl mx-auto">
          <header className="animate-fade-up">
            <p className="btn-mono text-muted">send</p>
            <h1 className="mt-3 text-4xl md:text-5xl font-display tracking-tight leading-[0.98]">
              Send a Boon.
            </h1>
            <p className="mt-4 max-w-lg text-base text-muted leading-relaxed">
              Recognize a person or agent. Review an x402 tool you would use again.
            </p>
          </header>

          <section className="mt-7 md:mt-8 rounded-xl border border-faint bg-paper-deep/35 p-5 sm:p-6 animate-fade-up" style={{ animationDelay: "120ms" }} aria-labelledby="x402-review-heading">
            <h2 id="x402-review-heading" className="text-2xl font-display tracking-tight text-ink">
              Review x402 tools with your agent.
            </h2>
            <p className="mt-3 max-w-xl text-sm text-muted leading-relaxed">
              After an x402 tool runs, ask your agent to record the outcome with Boon. The skill carries the route, wallet, and strongest available evidence into a review with verifiable context.
            </p>

            <div className="relative mt-5">
              <details className="overflow-hidden rounded-lg border border-faint bg-paper group">
                <summary className="flex min-h-14 cursor-pointer list-none select-none items-center justify-between gap-3 px-4 py-3 sm:px-5">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-olive-soft text-olive-deep" aria-hidden="true">
                      <SkillGlyph className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-ink">Boon Agent Prompt</span>
                      <span className="mt-0.5 block btn-mono text-[10px] text-muted">route evidence + review publishing</span>
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <span className="h-8 w-16" aria-hidden="true" />
                    <span className="btn-mono text-[10px] text-olive-deep">
                      <span className="group-open:hidden">Show ↓</span>
                      <span className="hidden group-open:inline">Hide ↑</span>
                    </span>
                  </span>
                </summary>

                <div className="border-t border-faint px-4 py-4 sm:px-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">Copy into your agent</p>
                    <div className="flex flex-wrap items-center gap-3">
                      <a
                        href="https://docs.boonprotocol.com/skill.md"
                        className="btn-mono text-[10px] text-olive-deep underline decoration-faint underline-offset-4 hover:decoration-olive-deep"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open skill.md ↗
                      </a>
                      <button
                        type="button"
                        className="min-h-9 rounded-md border border-faint bg-paper-deep/60 px-3 btn-mono text-[10px] text-olive-deep hover:border-olive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2"
                        onClick={() => void copyReviewPrompt()}
                      >
                        {promptCopied ? "Copied" : "Copy prompt"}
                      </button>
                    </div>
                  </div>
                  <pre className="mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-paper-deep/60 p-3 btn-mono text-xs text-ink-soft leading-relaxed selection:bg-olive-soft">
                    {X402_REVIEW_PROMPT}
                  </pre>
                  <p className="sr-only" aria-live="polite">{promptCopied ? "Agent prompt copied to clipboard." : ""}</p>
                </div>
              </details>
              <button
                type="button"
                className="absolute right-[4.5rem] top-[14px] z-10 min-h-8 min-w-16 rounded-md border border-faint bg-paper-deep/60 px-2.5 btn-mono text-[10px] text-olive-deep hover:border-olive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2"
                onClick={() => void copyReviewPrompt()}
              >
                {promptCopied ? "Copied" : "Copy"}
              </button>
            </div>
          </section>

          <RecentX402Reviews />

          <section className="mt-10" aria-labelledby="recipient-help-heading">
            <h2 id="recipient-help-heading" className="text-lg font-display tracking-tight text-ink">Who can receive a Boon?</h2>
            <p className="mt-2 text-sm text-muted leading-relaxed">
              Send one to an ERC-8004 agent, a GitHub handle, or an X handle. If you only know an agent&apos;s name, find its Base agent number on{" "}
              <a
                href="https://8004scan.io"
                className="btn-mono underline decoration-faint underline-offset-2 hover:text-ink hover:decoration-ink"
                target="_blank"
                rel="noopener noreferrer"
              >
                8004scan ↗
              </a>
              . For agent-to-agent workflows, use the{" "}
              <a
                href="https://docs.boonprotocol.com/skill.md"
                className="btn-mono underline decoration-faint underline-offset-2 hover:text-ink hover:decoration-ink"
                target="_blank"
                rel="noopener noreferrer"
              >
                Boon skill ↗
              </a>
              .
            </p>
          </section>

          <div className="mt-10 md:mt-12 pb-4">
            <SendWidget />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

type GlyphProps = { className?: string };
async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through for browsers that expose Clipboard API but block the write.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function SkillGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v5h5M10 12h5M10 16h5" />
    </svg>
  );
}
